/**
 * WebAuthn / Passkey browser helpers.
 *
 * Handles base64url <-> ArrayBuffer conversion (the WebAuthn API uses binary
 * buffers, but our backend speaks base64url JSON) and offers two top-level
 * flows: `registerPasskey()` and `signInWithPasskey()`.
 */
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

function bufferToBase64url(buffer) {
  const bytes = new Uint8Array(buffer);
  let binary = '';
  for (let i = 0; i < bytes.byteLength; i++) binary += String.fromCharCode(bytes[i]);
  return window.btoa(binary).replace(/\+/g, '-').replace(/\//g, '_').replace(/=+$/g, '');
}

function base64urlToBuffer(b64u) {
  const base64 = b64u.replace(/-/g, '+').replace(/_/g, '/');
  const pad = base64.length % 4 === 0 ? '' : '='.repeat(4 - (base64.length % 4));
  const binary = window.atob(base64 + pad);
  const bytes = new Uint8Array(binary.length);
  for (let i = 0; i < binary.length; i++) bytes[i] = binary.charCodeAt(i);
  return bytes.buffer;
}

function mapRegistrationOptions(json) {
  return {
    ...json,
    challenge: base64urlToBuffer(json.challenge),
    user: { ...json.user, id: base64urlToBuffer(json.user.id) },
    excludeCredentials: (json.excludeCredentials || []).map((c) => ({
      ...c, id: base64urlToBuffer(c.id),
    })),
  };
}

function mapAuthenticationOptions(json) {
  return {
    ...json,
    challenge: base64urlToBuffer(json.challenge),
    allowCredentials: (json.allowCredentials || []).map((c) => ({
      ...c, id: base64urlToBuffer(c.id),
    })),
  };
}

function serializeRegistration(cred) {
  const resp = cred.response;
  return {
    id: cred.id,
    type: cred.type,
    rawId: bufferToBase64url(cred.rawId),
    clientExtensionResults: cred.getClientExtensionResults?.() || {},
    response: {
      clientDataJSON: bufferToBase64url(resp.clientDataJSON),
      attestationObject: bufferToBase64url(resp.attestationObject),
      transports: resp.getTransports?.() || [],
    },
  };
}

function serializeAuthentication(cred) {
  const resp = cred.response;
  return {
    id: cred.id,
    type: cred.type,
    rawId: bufferToBase64url(cred.rawId),
    clientExtensionResults: cred.getClientExtensionResults?.() || {},
    response: {
      clientDataJSON: bufferToBase64url(resp.clientDataJSON),
      authenticatorData: bufferToBase64url(resp.authenticatorData),
      signature: bufferToBase64url(resp.signature),
      userHandle: resp.userHandle ? bufferToBase64url(resp.userHandle) : null,
    },
  };
}

export function isWebAuthnSupported() {
  return typeof window !== 'undefined'
    && typeof window.PublicKeyCredential !== 'undefined'
    && typeof navigator.credentials?.create === 'function';
}

export async function registerPasskey({ label }) {
  if (!isWebAuthnSupported()) throw new Error('Your browser does not support passkeys.');
  const { data: optionsJson } = await axios.post(`${API}/webauthn/register/begin`, {});
  const publicKey = mapRegistrationOptions(optionsJson);
  const cred = await navigator.credentials.create({ publicKey });
  if (!cred) throw new Error('No credential returned by the authenticator.');
  const payload = serializeRegistration(cred);
  const { data } = await axios.post(`${API}/webauthn/register/complete`, {
    credential: payload, label: label || null,
  });
  return data;
}

export async function signInWithPasskey({ email }) {
  if (!isWebAuthnSupported()) throw new Error('Your browser does not support passkeys.');
  if (!email) throw new Error('Email is required.');
  const { data: optionsJson } = await axios.post(`${API}/webauthn/authenticate/begin`, { email });
  const publicKey = mapAuthenticationOptions(optionsJson);
  const cred = await navigator.credentials.get({ publicKey });
  if (!cred) throw new Error('No credential returned by the authenticator.');
  const payload = serializeAuthentication(cred);
  const { data } = await axios.post(`${API}/webauthn/authenticate/complete`, {
    credential: payload, email,
  });
  return data.user;
}
